/**
 * Guardrail DocGuard
 * 
 * Documentation quality control system that prevents drift, detects slop,
 * ensures documentation stays grounded in reality, and tracks task progress.
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { glob } from 'fast-glob';
import type { DocGuardConfig, DocGuardResult, DocGuardIssue, DuplicateCheckResult } from '../types.js';

// Re-export TaskTracker and CompletionGuard
export * from './TaskTracker.js';
export * from './CompletionGuard.js';

// Re-export enhanced DocGuard engine
export { CodebaseAnalyzer } from './CodebaseAnalyzer.js';
export type { CodebaseSnapshot, ModuleInfo, ExportEntry, PackageMeta, RouteEntry } from './CodebaseAnalyzer.js';
export { DocMapper } from './DocMapper.js';
export type { DocFile, DocMapping, ManagedSection, StaleDoc } from './DocMapper.js';
export { DocGenerator } from './DocGenerator.js';
export type { GeneratedDoc, GeneratorOptions } from './DocGenerator.js';
export { DocGuardEngine } from './DocGuardEngine.js';
export type { DocGuardSyncResult, DocGuardStatus, DocGuardDiff, DocGuardEngineConfig } from './DocGuardEngine.js';

// Slop phrases that indicate low-quality AI-generated content
const SLOP_PHRASES = [
  'in this guide we will explore',
  "let's dive into",
  'it is important to note',
  'best practices suggest',
  'seamlessly integrates',
  'this comprehensive guide',
  'in order to',
  'it should be noted that',
  'without further ado',
  'at the end of the day',
  'paradigm shift',
  'synergy',
  'leverage',
  'utilize',
  'cutting-edge',
  'state-of-the-art',
  'revolutionary',
  'game-changing',
  'empower',
  'holistic approach',
];

// Fluff words that add no value
const FLUFF_WORDS = [
  'very', 'really', 'actually', 'basically', 'essentially',
  'literally', 'simply', 'incredibly', 'absolutely', 'comprehensive',
  'robust', 'seamless', 'elegant', 'powerful', 'innovative',
];

// Reality anchor patterns
const ANCHOR_PATTERNS = [
  /`[a-zA-Z0-9_\-./]+\.(ts|js|tsx|jsx|json|md|css|html)`/,  // File paths
  /`(npm|npx|yarn|pnpm)\s+[a-z]+/,                          // CLI commands
  /`(GET|POST|PUT|PATCH|DELETE)\s+\/[a-zA-Z0-9/_-]+`/,      // API endpoints
  /`[A-Z_]+`/,                                               // Environment variables
  /line\s+\d+/i,                                             // Line references
  /```[\s\S]+```/,                                           // Code blocks
];

export class DocGuard {
  private projectRoot: string;
  private config: DocGuardConfig;
  private documentRegistry: Map<string, DocumentInfo> = new Map();

  constructor(projectRoot: string, config: DocGuardConfig) {
    this.projectRoot = projectRoot;
    this.config = config;
  }

  /**
   * Evaluate a document for quality
   */
  async evaluate(content: string, filePath?: string): Promise<DocGuardResult> {
    const issues: DocGuardIssue[] = [];
    const anchors: string[] = [];
    const slopPhrases: string[] = [];

    // Check for slop phrases
    const contentLower = content.toLowerCase();
    for (const phrase of SLOP_PHRASES) {
      if (contentLower.includes(phrase)) {
        slopPhrases.push(phrase);
        issues.push({
          type: 'slop-phrase',
          message: `Detected slop phrase: "${phrase}"`,
          suggestion: 'Remove or rephrase with specific, concrete language',
        });
      }
    }

    // Calculate fluff ratio
    const words = content.split(/\s+/);
    const fluffCount = words.filter(w => 
      FLUFF_WORDS.includes(w.toLowerCase().replace(/[^a-z]/g, ''))
    ).length;
    const fluffRatio = words.length > 0 ? fluffCount / words.length : 0;

    if (fluffRatio > this.config.maxFluffRatio) {
      issues.push({
        type: 'high-fluff',
        message: `High fluff ratio: ${(fluffRatio * 100).toFixed(1)}% (max: ${(this.config.maxFluffRatio * 100).toFixed(0)}%)`,
        suggestion: 'Remove unnecessary filler words',
      });
    }

    // Count reality anchors
    for (const pattern of ANCHOR_PATTERNS) {
      const matches = content.match(new RegExp(pattern, 'g'));
      if (matches) {
        anchors.push(...matches);
      }
    }

    if (anchors.length < this.config.minAnchors) {
      issues.push({
        type: 'missing-anchor',
        message: `Only ${anchors.length} reality anchors found (minimum: ${this.config.minAnchors})`,
        suggestion: 'Add file paths, code examples, CLI commands, or API endpoints',
      });
    }

    // Check for code example
    const hasCodeBlock = /```[\s\S]+```/.test(content);
    if (!hasCodeBlock && words.length > 100) {
      issues.push({
        type: 'no-example',
        message: 'No code example found in document',
        suggestion: 'Add at least one runnable code block',
      });
    }

    // Check for duplicate if file path provided
    if (filePath) {
      const duplicateCheck = await this.checkDuplicate(content, filePath);
      if (duplicateCheck.isDuplicate) {
        issues.push({
          type: 'duplicate',
          message: `Similar content found in ${duplicateCheck.existingFile}`,
          suggestion: duplicateCheck.suggestion === 'merge' 
            ? 'Consider merging with existing document'
            : 'Update existing document instead',
        });
      }
    }

    // Calculate quality score
    let score = 100;
    score -= slopPhrases.length * 10;
    score -= Math.max(0, (fluffRatio - this.config.maxFluffRatio) * 100);
    score -= Math.max(0, (this.config.minAnchors - anchors.length) * 15);
    score -= issues.filter(i => i.type === 'no-example').length * 10;
    score -= issues.filter(i => i.type === 'duplicate').length * 20;
    score = Math.max(0, Math.min(100, score));

    return {
      valid: issues.length === 0,
      score,
      issues,
      anchors,
      slopPhrases,
      fluffRatio,
    };
  }

  /**
   * Quick slop check - fast validation
   */
  quickCheck(content: string): { hasSop: boolean; phrases: string[] } {
    const phrases: string[] = [];
    const contentLower = content.toLowerCase();
    
    for (const phrase of SLOP_PHRASES) {
      if (contentLower.includes(phrase)) {
        phrases.push(phrase);
      }
    }

    return {
      hasSop: phrases.length > 0,
      phrases,
    };
  }

  /**
   * Check for duplicate content
   */
  async checkDuplicate(content: string, filePath: string): Promise<DuplicateCheckResult> {
    await this.loadDocumentRegistry();

    const contentHash = this.computeSimHash(content);
    const contentWords = new Set(content.toLowerCase().split(/\s+/));

    for (const [existingPath, info] of this.documentRegistry) {
      if (existingPath === filePath) continue;

      // Check Jaccard similarity
      const existingWords = new Set(info.content.toLowerCase().split(/\s+/));
      const intersection = new Set([...contentWords].filter(w => existingWords.has(w)));
      const union = new Set([...contentWords, ...existingWords]);
      const similarity = intersection.size / union.size;

      if (similarity > this.config.similarityThreshold) {
        return {
          isDuplicate: true,
          similarity,
          existingFile: existingPath,
          existingContent: info.content.substring(0, 200) + '...',
          suggestion: similarity > 0.9 ? 'update' : 'merge',
        };
      }
    }

    return {
      isDuplicate: false,
      similarity: 0,
    };
  }

  /**
   * Scan all documentation and update registry
   */
  async scanDocuments(): Promise<void> {
    const docFiles = await glob(['**/*.md'], {
      cwd: this.projectRoot,
      ignore: ['**/node_modules/**', ...this.config.ignorePatterns],
      absolute: true,
    });

    this.documentRegistry.clear();

    for (const file of docFiles) {
      try {
        const content = await fs.readFile(file, 'utf-8');
        const relativePath = path.relative(this.projectRoot, file);
        
        this.documentRegistry.set(relativePath, {
          path: relativePath,
          content,
          hash: this.computeSimHash(content),
          lastModified: new Date(),
        });
      } catch {
        // Skip unreadable files
      }
    }
  }

  /**
   * Find canonical document for a topic
   */
  async findCanonical(topic: string): Promise<string | null> {
    await this.loadDocumentRegistry();

    const topicWords = new Set(topic.toLowerCase().split(/\s+/));

    let bestMatch: { path: string; score: number } | null = null;

    for (const [docPath, info] of this.documentRegistry) {
      const titleMatch = docPath.toLowerCase();
      const contentWords = new Set(info.content.toLowerCase().split(/\s+/));
      
      // Check title match
      let score = 0;
      for (const word of topicWords) {
        if (titleMatch.includes(word)) score += 10;
        if (contentWords.has(word)) score += 1;
      }

      if (score > 0 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { path: docPath, score };
      }
    }

    return bestMatch?.path || null;
  }

  /**
   * Get suggestion for where to place content
   */
  suggestLocation(topic: string): string {
    // Default doc locations based on topic
    const topicLower = topic.toLowerCase();
    
    if (topicLower.includes('api') || topicLower.includes('endpoint')) {
      return 'docs/api/';
    }
    if (topicLower.includes('setup') || topicLower.includes('install')) {
      return 'docs/getting-started/';
    }
    if (topicLower.includes('architecture') || topicLower.includes('design')) {
      return 'docs/architecture/';
    }
    if (topicLower.includes('guide') || topicLower.includes('tutorial')) {
      return 'docs/guides/';
    }
    
    return 'docs/';
  }

  /**
   * Get registry statistics
   */
  getStats(): { totalDocs: number; avgScore: number; duplicates: number } {
    return {
      totalDocs: this.documentRegistry.size,
      avgScore: 0, // Would require evaluating all docs
      duplicates: 0, // Would require checking all pairs
    };
  }

  private async loadDocumentRegistry(): Promise<void> {
    if (this.documentRegistry.size === 0) {
      await this.scanDocuments();
    }
  }

  private computeSimHash(content: string): string {
    // Simple hash for now
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }
}

interface DocumentInfo {
  path: string;
  content: string;
  hash: string;
  lastModified: Date;
}

/**
 * Create a DocGuard instance
 */
export function createDocGuard(projectRoot: string, config: DocGuardConfig): DocGuard {
  return new DocGuard(projectRoot, config);
}

/**
 * Quick evaluation of document content
 */
export async function evaluateDoc(
  content: string, 
  projectRoot: string, 
  config: DocGuardConfig
): Promise<DocGuardResult> {
  const guard = new DocGuard(projectRoot, config);
  return guard.evaluate(content);
}

/**
 * Check if content should be blocked due to quality issues
 */
export function shouldBlock(result: DocGuardResult, strictMode: boolean): boolean {
  if (strictMode) {
    return !result.valid || result.score < 70;
  }
  return result.score < 50;
}
